import React from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StatusChip } from 'src/components/payments-ops/StatusChip'
import { Button } from 'src/components/shared/Button'
import {
  achTypeLabels,
  earningOrderStatusLabels,
  usBankDestinationTypeLabels,
} from 'src/constants/payments-ops/labels'
import type { AchType, EarningOrderStatus } from 'src/constants/payments-ops/enums'
import type { UsBankEarning } from 'src/interfaces/usBankTransactions'
import {
  formatDateOnly,
  formatDateTime,
  parseFlexibleDateTime,
  parseIsoDate,
  parseUnixTimestamp,
} from 'src/utils/grid'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { maskAccountNumber } from 'src/utils/paymentsOps/maskAccountNumber'

const moneyColumn = (field: keyof UsBankEarning, headerName: string): GridColDef<UsBankEarning> => {
  return {
    field,
    headerName,
    width: 130,
    align: 'right',
    headerAlign: 'right',
    sortable: false,
    valueFormatter: (value: number | null | undefined) => {
      return formatCentsAsUsd(value)
    },
  }
}

const monoColumn = (field: keyof UsBankEarning, headerName: string): GridColDef<UsBankEarning> => {
  return {
    field,
    headerName,
    width: 160,
    sortable: false,
    renderCell: (params: GridRenderCellParams<UsBankEarning, string | null | undefined>) => {
      return <span css={tw`truncate font-mono text-xs`}>{params.value ?? '—'}</span>
    },
  }
}

/** Server-ordered by `uploadedAt DESC`; every column is `sortable: false`. */
export const buildUsBankEarningColumns = (
  onShowOrders: (earning: UsBankEarning) => void,
): Array<GridColDef<UsBankEarning>> => {
  return [
    monoColumn('earningId', 'Earning ID'),
    { field: 'partnerDisplayName', headerName: 'Partner', flex: 1, minWidth: 160, sortable: false },
    {
      field: 'contractorName',
      headerName: 'Contractor',
      flex: 1,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<UsBankEarning>) => {
        return (
          <div css={tw`flex min-w-0 flex-col`}>
            <span css={tw`truncate`}>{params.row.contractorName || '—'}</span>
            <Typography variant="body2" css={tw`truncate text-xs text-shades-500`}>
              {params.row.contractorExternalId}
            </Typography>
          </div>
        )
      },
    },
    moneyColumn('earningAmount', 'Earning amount'),
    moneyColumn('fee', 'Fee'),
    moneyColumn('orderAmount', 'Net order amount'),
    {
      field: 'orderStatus',
      headerName: 'Order status',
      width: 160,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<UsBankEarning, EarningOrderStatus | null>) => {
        const status = params.value ? earningOrderStatusLabels[params.value] : undefined

        return status ? <StatusChip label={status.label} tone={status.tone} /> : <span>—</span>
      },
    },
    {
      field: 'orderUsBankStatus',
      headerName: 'US Bank status',
      width: 190,
      sortable: false,
      valueGetter: (value: string | null | undefined) => {
        return value ?? '—'
      },
    },
    {
      field: 'orderExpectedSettlementDate',
      headerName: 'Expected settlement',
      width: 160,
      sortable: false,
      valueGetter: parseIsoDate,
      valueFormatter: formatDateOnly,
    },
    {
      field: 'destinationType',
      headerName: 'Destination',
      width: 170,
      sortable: false,
      valueGetter: (_value: unknown, row: UsBankEarning) => {
        const type = row.destinationType ? usBankDestinationTypeLabels[row.destinationType] : '—'

        return row.orderDestinationLast4
          ? `${type} ${maskAccountNumber(row.orderDestinationLast4)}`
          : type
      },
    },
    {
      field: 'orderAchType',
      headerName: 'ACH type',
      width: 110,
      sortable: false,
      valueGetter: (value: AchType | null | undefined) => {
        return value ? achTypeLabels[value] : '—'
      },
    },
    monoColumn('orderUsBankTransactionId', 'US Bank tx ID'),
    monoColumn('orderId', 'Order ID'),
    {
      field: 'earningUploadedAt',
      headerName: 'Uploaded at',
      width: 180,
      sortable: false,
      valueGetter: parseFlexibleDateTime,
      valueFormatter: formatDateTime,
    },
    {
      field: 'earningCreatedAt',
      headerName: 'Created at',
      width: 180,
      sortable: false,
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateTime,
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<UsBankEarning>) => {
        return (
          <Button
            size="small"
            color="white"
            onClick={() => {
              onShowOrders(params.row)
            }}
          >
            Orders
          </Button>
        )
      },
    },
  ]
}
